import type {
  GlobalDefaultParityResponse,
  RoutingDefaultParityPolicyDiff,
  RoutingDefaultParityTargetDiff,
  RoutingDefaultParityTargetRow,
} from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { useGlobalDefaultParity } from '../../api/useTenantRouting.ts';
import { Chip, Dialog, Table, TBody, Td, Th, THead, Tr } from '../../ui/index.ts';

/**
 * On-demand comparison of the two surfaces that both answer to "the default
 * route" (#1918).
 *
 * Severity is the part operators get wrong, and it is not a property of the
 * difference alone -- it is the difference crossed with the coverage mode. The
 * server does that crossing and reports `counts.live_diffs`; this dialog
 * renders that split rather than a flat count, because presenting an inert
 * bookkeeping gap as an outage trains people to ignore the one case that is an
 * outage: under `exclusive`, a SKU only the platform default table carries has
 * no candidates at all and 404s for every org on the global default.
 */

/**
 * `I18nKey` is a closed union, so a template-literal key would not typecheck —
 * and that is the right constraint: it keeps every rendered string greppable
 * in the locale files.
 */
const KIND_KEYS = {
  missing_in_config: 'tenant_routing.parity.kind_missing_in_config',
  missing_in_default: 'tenant_routing.parity.kind_missing_in_default',
  mismatched: 'tenant_routing.parity.kind_mismatched',
} as const;

/**
 * Every field the server's signature compares, and no more.
 *
 * Printing a subset is worse than printing nothing: a pair differing only in
 * `enabled` or `priority` would render as two byte-identical cells under a
 * heading that says they differ, and an operator who sees that once stops
 * believing the tool.
 */
function describeRows(rows: readonly RoutingDefaultParityTargetRow[]): string {
  if (rows.length === 0) return '—';
  return rows
    .map((row) => {
      const identity = row.upstream_channel_slug || row.upstream_channel_id;
      const retry = row.retry_order === null ? '—' : String(row.retry_order);
      const enabled = row.enabled ? '' : ', off';
      return `${identity} (w${String(row.weight)}, r${retry}, p${String(row.priority)}${enabled})`;
    })
    .join('; ');
}

function TargetDiffTable({ diffs }: { readonly diffs: readonly RoutingDefaultParityTargetDiff[] }): JSX.Element {
  const t = useT();
  return (
    <Table>
      <THead>
        <Tr interactive={false}>
          <Th>{t('tenant_routing.parity.column_sku')}</Th>
          <Th>{t('tenant_routing.parity.column_kind')}</Th>
          <Th>{t('tenant_routing.parity.column_config')}</Th>
          <Th>{t('tenant_routing.parity.column_default')}</Th>
        </Tr>
      </THead>
      <TBody>
        {diffs.map((diff) => (
          <Tr key={`target-${diff.sku_id}`}>
            <Td>{diff.sku_name}</Td>
            <Td>{t(KIND_KEYS[diff.kind])}</Td>
            <Td>{describeRows(diff.config_rows)}</Td>
            <Td>{describeRows(diff.default_rows)}</Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

function PolicyDiffTable({ diffs }: { readonly diffs: readonly RoutingDefaultParityPolicyDiff[] }): JSX.Element {
  const t = useT();
  return (
    <Table>
      <THead>
        <Tr interactive={false}>
          <Th>{t('tenant_routing.parity.column_sku')}</Th>
          <Th>{t('tenant_routing.parity.column_kind')}</Th>
          <Th>{t('tenant_routing.parity.column_config')}</Th>
          <Th>{t('tenant_routing.parity.column_default')}</Th>
        </Tr>
      </THead>
      <TBody>
        {diffs.map((diff) => (
          <Tr key={`policy-${diff.sku_id}`}>
            <Td>{diff.sku_name}</Td>
            <Td>{t(KIND_KEYS[diff.kind])}</Td>
            <Td>{diff.config_policy_mode ?? '—'}</Td>
            <Td>{diff.default_policy_mode ?? '—'}</Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

function ParityBody({ parity }: { readonly parity: GlobalDefaultParityResponse }): JSX.Element {
  const t = useT();
  if (parity.global_config_id === null) {
    return <p className="m-0 text-dense text-paper-low">{t('tenant_routing.parity.no_global_default')}</p>;
  }
  if (parity.in_sync) {
    return (
      <div className="flex flex-col gap-2">
        <Chip tone="ok">{t('tenant_routing.parity.in_sync')}</Chip>
        <p className="m-0 text-dense text-paper-low">
          {t('tenant_routing.parity.in_sync_body', {
            configSkus: String(parity.counts.config_skus),
          })}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Chip tone={parity.counts.live_diffs > 0 ? 'bad' : 'warn'}>
          {t('tenant_routing.parity.drift', {
            targets: String(parity.counts.target_diffs),
            policies: String(parity.counts.policy_diffs),
          })}
        </Chip>
        <p className="m-0 text-dense leading-5 text-paper-low">
          {parity.counts.live_diffs > 0
            ? t('tenant_routing.parity.drift_body_live', {
                count: String(parity.counts.live_diffs),
              })
            : t('tenant_routing.parity.drift_body_latent', {
                mode: parity.coverage_mode ?? '—',
              })}
        </p>
      </div>
      {parity.target_diffs.length === 0 ? null : (
        <section className="flex flex-col gap-1.5">
          <span className="text-label text-paper-low">{t('tenant_routing.parity.section_targets')}</span>
          <TargetDiffTable diffs={parity.target_diffs} />
        </section>
      )}
      {parity.policy_diffs.length === 0 ? null : (
        <section className="flex flex-col gap-1.5">
          <span className="text-label text-paper-low">{t('tenant_routing.parity.section_policies')}</span>
          <PolicyDiffTable diffs={parity.policy_diffs} />
        </section>
      )}
    </div>
  );
}

export function GlobalDefaultParityDialog({
  open,
  onOpenChange,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
}): JSX.Element {
  const t = useT();
  const parity = useGlobalDefaultParity(open);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('tenant_routing.parity.title')}
      description={t('tenant_routing.parity.description')}
      size="xl"
      closeLabel={t('common.close')}
      testId="tenant-routing-parity-dialog"
    >
      {parity.isPending ? (
        <p className="m-0 text-dense text-paper-low">{t('tenant_routing.parity.loading')}</p>
      ) : parity.data === undefined ? (
        <p className="m-0 text-dense text-warn">{t('tenant_routing.parity.failed')}</p>
      ) : (
        <ParityBody parity={parity.data} />
      )}
    </Dialog>
  );
}
